import { faChevronRight, faChevronLeft, faClock, faSearch, faCheckCircle, faSpinner } from "@fortawesome/pro-regular-svg-icons"
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Form } from "react-bootstrap"
import React, { useState, useEffect, useContext, useRef } from "react"
import useStockModulesDB from "./shared/useStockModulesDB"

import { FirebaseContext } from "../Firebase"
import FuzzySearch from "fuzzy-search"

import "./work-readiness.css"
import { FormattedMessage, useIntl } from "react-intl"
import Link, { navigate } from "../../link"
import useSchoolFilteredProgramsDB from "./shared/useSchoolFilteredProgramsDB"
import { RadioButton } from "primereact/radiobutton"
import { truncateText } from "./shared/utils"
import DOMPurify from "dompurify"
import { Dropdown } from "semantic-ui-react"
import { ImageWithFallback } from "./views/display"

/**
 * Work Readiness tab component, displays either K-12 or Adult work readiness activities.
 *
 * @returns JSX component
 */
const WorkReadinessTab = () => {
  const { formatMessage } = useIntl()
  const { firebase } = useContext(FirebaseContext)
  const [curriculumType, setCurriculumType] = useState("MODULES") // Initially null to wait for assignments to load
  const [assignments, setAssignments] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const res = await firebase.getModulesAssignedToUser() // Fetch assignments
        const fetchedAssignments = res?.data || []
        setAssignments(fetchedAssignments)

        // If there are active assignments, show "Assignments" tab first
        if (fetchedAssignments.length > 0) {
          setCurriculumType("ASSIGNMENTS")
        } else {
          setCurriculumType("MODULES")
        }
      } catch (error) {
        console.error("Error fetching assignments:", error)
        setCurriculumType("MODULES") // Default to "Programs" if there's an error
      } finally {
        setIsLoading(false)
      }
    }

    fetchAssignments()
  }, [firebase])

  return (
    <div className="work-readiness">
      <div className="inner work-readiness-introduction">
        <h2>
          <FormattedMessage id="dashboard-tab-work-readiness" defaultMessage="Work Readiness" />
        </h2>
        <>
          <p>
            <FormattedMessage
              id="work-readiness-para-1"
              defaultMessage="Prepare for success in the workplace! Learn how to advocate for
              yourself and others, chart an inspiring path for your future,
              develop plans for your future, master decision-making, immerse
              yourself in the principles of equity and inclusion practices, and
              hone your listening skills."
            />
          </p>
          <p>
            <FormattedMessage id="work-readiness-para-2" defaultMessage="Activities are based on the NACE competency framework." />
          </p>
        </>
      </div>

      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "20vh" }}>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#00b8ef" />
        </div>
      ) : (
        <Curriculum curriculumType={curriculumType} setCurriculumType={setCurriculumType} assignments={assignments} />
      )}
    </div>
  )
}
const Curriculum = ({ curriculumType, setCurriculumType, assignments }) => {
  return (
    <div className="curriculum">
      <div className="curriculum-header">
        <label className={curriculumType === "MODULES" ? "active" : ""} onClick={() => setCurriculumType("MODULES")}>
          <FormattedMessage id="curriculum-all-modules" defaultMessage="all modules" />
        </label>{" "}
        |
        <label className={curriculumType === "PROGRAMS" ? "active" : ""} onClick={() => setCurriculumType("PROGRAMS")}>
          <FormattedMessage id="curriculum-all-programs" defaultMessage="all programs" />
        </label>{" "}
        {assignments.length > 0 && (
          <>
            {" "}
            |
            <label className={curriculumType === "ASSIGNMENTS" ? "active" : ""} onClick={() => setCurriculumType("ASSIGNMENTS")}>
              <FormattedMessage id="curriculum-assignments" defaultMessage="assignments" />
            </label>
          </>
        )}
      </div>
      {curriculumType === "PROGRAMS" && <CurriculumPrograms />}
      {curriculumType === "ASSIGNMENTS" && <CurriculumAssignments assignments={assignments} />}
      {curriculumType === "MODULES" && <CurriculumModules />}
    </div>
  )
}

const CurriculumAssignments = ({ assignments }) => {
  const { formatMessage, locale } = useIntl()
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("ALL")
  const [completionStatusSort, setCompletionStatusSort] = useState("ALL")
  const [currentPage, setCurrentPage] = useState(1)
  const scrollRef = useRef(null) // Create a ref for scrolling
  const itemsPerPage = 12

  const searcher = new FuzzySearch(assignments, ["title"], {
    caseSensitive: false,
  })

  let filteredAssignments = search ? searcher.search(search) : assignments

  if (category !== "ALL") {
    filteredAssignments = filteredAssignments.filter((program) => program.category === category)
  }

  if (completionStatusSort !== "ALL") {
    filteredAssignments = filteredAssignments.filter((program) => program.status === completionStatusSort)
  }

  const uniqueCategories = ["ALL", ...new Set(assignments.map((assignment) => assignment.category).filter(category => category))].sort((a, b) => {
    if (a === "ALL") return -1
    if (b === "ALL") return 1
    return a.localeCompare(b)
  })

  const categoryToText = { "ALL": formatMessage({ id: "curriculum-ALL", defaultMessage: "ALL" }) }
  filteredAssignments.forEach(assignment => {
    if (locale === "es-ES" && assignment.category && assignment.category_es) {
      categoryToText[assignment.category] = assignment.category_es
    } else if (locale === "en-US" && assignment.category) {
      categoryToText[assignment.category] = assignment.category
    }
  })

  const renderCurriculum = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentAssignments = filteredAssignments.slice(startIndex, endIndex)

    return currentAssignments.map((assignment, index) => <CurriculumAssignmentCard key={index} assignment={assignment} />)
  }

  const handleCategoryChange = (event) => {
    setCategory(event.target.value)
  }

  const handleCompletionStatusSortChange = (status) => {
    setSearch("") // Clear the search input field
    setCategory("ALL") // Reset the category to "ALL"
    setCompletionStatusSort(status) // Update the completion status sort
    setCurrentPage(1) // Reset to the first page
  }

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1)
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }, 100)
  }

  const totalPages = Math.ceil(filteredAssignments.length / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, filteredAssignments.length)

  return (
    <>
      <div className="curriculum-filters" ref={scrollRef}>
        <div className="search">
          <input
            type="text"
            placeholder={formatMessage({ id: "curriculum-module-name", defaultMessage: "Module Name" })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="search-button">
            <FontAwesomeIcon icon={faSearch} color="#363636" size="lg" />
          </div>
        </div>
        <Dropdown
          placeholder="Select Category"
          selection
          options={uniqueCategories.map((cat) => ({
            key: cat,
            text: categoryToText[cat] || cat,
            value: cat,
          }))}
          icon="chevron down"
          value={category}
          onChange={(e, { value }) => handleCategoryChange({ target: { value } })}
        />
        <div className="radio-group">
          <FormattedMessage id="curriculum-sort-by" defaultMessage="Sort by:" />{" "}
          <div>
            <label>
              <RadioButton
                inputId="all"
                name="choice"
                value="ALL"
                checked={completionStatusSort === "ALL"}
                onChange={() => handleCompletionStatusSortChange("ALL")}
              />
              <span>
                <FormattedMessage id="curriculum-all" defaultMessage="All" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="in_progress"
                name="choice"
                value="in_progress"
                checked={completionStatusSort === "in_progress"}
                onChange={() => handleCompletionStatusSortChange("in_progress")}
              />
              <span>
                <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="completed"
                name="choice"
                value="completed"
                checked={completionStatusSort === "completed"}
                onChange={() => handleCompletionStatusSortChange("completed")}
              />
              <span>
                <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="not_started"
                name="choice"
                value="not_started"
                checked={completionStatusSort === "not_started"}
                onChange={() => handleCompletionStatusSortChange("not_started")}
              />
              <span>
                <FormattedMessage id="curriculum-not-started" defaultMessage="Not Started" />
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="curriculum-content-grid">{renderCurriculum()}</div>
      <div className="pagination">
        <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>
          <FontAwesomeIcon icon={faChevronLeft} color="#406C70" />
        </button>
        <span>
          {startItem} - {endItem} <FormattedMessage id="curriculum-of" defaultMessage="of" /> {filteredAssignments.length}
        </span>
        <button onClick={() => handlePageChange("next")} disabled={currentPage === totalPages}>
          <FontAwesomeIcon icon={faChevronRight} color="#406C70" />
        </button>
      </div>
    </>
  )
}

const CurriculumModules = () => {
  const { firebase } = useContext(FirebaseContext)
  const { formatMessage, locale } = useIntl()
  const { stockModules, isLoading, error } = useStockModulesDB(firebase)
  const scrollRef = useRef(null) // Create a ref for the Curriculum component
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("ALL")
  const [completionStatusSort, setCompletionStatusSort] = useState("ALL")
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 12

  const searcher = new FuzzySearch(stockModules || [], ["title"], {
    caseSensitive: false,
  })

  let filteredModules = search ? searcher.search(search) : stockModules || []

  if (category !== "ALL") {
    filteredModules = filteredModules.filter((module) => module.category === category)
  }

  if (completionStatusSort !== "ALL") {
    filteredModules = filteredModules.filter((module) => module.status === completionStatusSort)
  }

  const uniqueCategories = ["ALL", ...new Set((stockModules || []).map((module) => module.category).filter(category => category))].sort((a, b) => {
    if (a === "ALL") return -1
    if (b === "ALL") return 1
    return a.localeCompare(b)
  })

  const categoryToText = { "ALL": formatMessage({ id: "curriculum-ALL", defaultMessage: "ALL" }) }
  filteredModules.forEach(module => {
    if (locale === "es-ES" && module.category && module.category_es) {
      categoryToText[module.category] = module.category_es
    } else if (locale === "en-US" && module.category) {
      categoryToText[module.category] = module.category
    }
  })

  const renderModules = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentModules = filteredModules.slice(startIndex, endIndex)

    return currentModules.map((module, index) => <CurriculumModuleCard key={index} module={module} />)
  }

  const handleCategoryChange = (event) => {
    setCategory(event.target.value)
  }

  const handleCompletionStatusSortChange = (status) => {
    setSearch("") // Clear the search input field
    setCategory("ALL") // Reset the category to "ALL" 
    setCompletionStatusSort(status) // Update the completion status sort
    setCurrentPage(1) // Reset to the first page
  }

  const handlePageChange = (direction) => {
    // Scroll to the top of the Curriculum component
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1)
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }, 100)
  }

  const totalPages = Math.ceil(filteredModules.length / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, filteredModules.length)

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "20vh" }}>
        <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#00b8ef" />
      </div>
    )
  }

  if (error) {
    return <div>Error loading modules: {error.message}</div>
  }

  return (
    <>
      <div className="curriculum-filters" ref={scrollRef}>
        <div className="search">
          <input
            type="text"
            placeholder={formatMessage({ id: "curriculum-module-name", defaultMessage: "Module Name" })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="search-button">
            <FontAwesomeIcon icon={faSearch} color="#363636" size="lg" />
          </div>
        </div>
        <Dropdown
          placeholder="Select Category"
          selection
          options={uniqueCategories.map((cat) => ({
            key: cat,
            text: categoryToText[cat] || cat,
            value: cat,
          }))}
          icon="chevron down"
          value={category}
          onChange={(e, { value }) => handleCategoryChange({ target: { value } })}
        />
        <div className="radio-group">
          <FormattedMessage id="curriculum-sort-by" defaultMessage="Sort by:" />{" "}
          <div>
            <label>
              <RadioButton
                inputId="all"
                name="choice"
                value="ALL"
                checked={completionStatusSort === "ALL"}
                onChange={() => handleCompletionStatusSortChange("ALL")}
              />
              <span>
                <FormattedMessage id="curriculum-all" defaultMessage="All" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="in_progress"
                name="choice"
                value="in_progress"
                checked={completionStatusSort === "in_progress"}
                onChange={() => handleCompletionStatusSortChange("in_progress")}
              />
              <span>
                <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="completed"
                name="choice"
                value="completed"
                checked={completionStatusSort === "completed"}
                onChange={() => handleCompletionStatusSortChange("completed")}
              />
              <span>
                <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="not_started"
                name="choice"
                value="not_started"
                checked={completionStatusSort === "not_started"}
                onChange={() => handleCompletionStatusSortChange("not_started")}
              />
              <span>
                <FormattedMessage id="curriculum-not-started" defaultMessage="Not Started" />
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="curriculum-content-grid">{renderModules()}</div>
      <div className="pagination">
        <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>
          <FontAwesomeIcon icon={faChevronLeft} color="#406C70" />
        </button>
        <span>
          {startItem} - {endItem} <FormattedMessage id="curriculum-of" defaultMessage="of" /> {filteredModules.length}
        </span>
        <button onClick={() => handlePageChange("next")} disabled={currentPage === totalPages}>
          <FontAwesomeIcon icon={faChevronRight} color="#406C70" />
        </button>
      </div>
    </>
  )
}

const CurriculumModuleCard = ({ module }) => {
  const { short_description, short_description_es, id, title, title_es, category, category_es, time, time_es, status, image, due_at, creator_first_name, creator_last_name, what_learn, what_learn_es } = module
  const { locale } = useIntl()
  const formattedDueDate = new Date(due_at).toLocaleDateString(locale)

  // Localize all displayed text
  let localizedTitle, localizedDescription, localizedCategory, localizedTime
  if (locale === "en-US" || !locale) {
    localizedTitle = title
    localizedDescription = short_description
    localizedCategory = category
    localizedTime = time
  } else if (locale === "es-ES") {
    localizedTitle = title_es || title
    localizedDescription = short_description_es || short_description
    localizedCategory = category_es || category
    localizedTime = time_es || time
  }

  const renderDescription = () => {
    const descriptionMaxLength = 75
    const localizedTruncatedText = truncateText(localizedDescription, descriptionMaxLength)
    return DOMPurify.sanitize(localizedTruncatedText)
  }

  const renderWhatLearn = () => {
    if (!what_learn) {
      return ""
    }
    // only include the first 3 bullet points
    // const whatLearnBullets = locale === "es-ES" ? what_learn_es : what_learn
    // const sanitizedBullets = DOMPurify.sanitize(whatLearnBullets)
    // const bulletPoints = sanitizedBullets.split("<li>").slice(1, 4).map((point, index) => `<li>${point}</li>`).join("")
    // return `<ul>${bulletPoints}</ul>`

    // strip away any bullet points that are not in the first 3
    const sanitizedWhatLearn = locale === "es-ES" ? what_learn_es : what_learn
    const bulletPoints = sanitizedWhatLearn.split("<li>").slice(1, 4).map((point) => `<li>${point}</li>`).join("")
    return `<ul>${bulletPoints}</ul>`

    // return DOMPurify.sanitize(locale === "es-ES" ? what_learn_es : what_learn)
  }

  const renderCompletionStatus = () => {
    switch (status) {
      case "completed":
        return (
          <span className="curriculum-assignment-card-footer-status" style={{ color: "#2DB76D" }}>
            <FontAwesomeIcon icon={faCheckCircle} />
            <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
          </span>
        )
      case "in_progress":
        return (
          <span className="curriculum-assignment-card-footer-status" style={{ color: "#E7812E" }}>
            <FontAwesomeIcon icon={faSpinnerThird} />
            <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
          </span>
        )
      default:
        return <span className="curriculum-assignment-card-footer-status"></span>
    }
  }

  const handleCardClick = () => {
    navigate(`${locale === "es-ES" ? "/es-ES" : ""}/modules/${id}`)
  }

  return (
    <div className="curriculum-assignment-card" onClick={handleCardClick}>
      <div>
        <div className="curriculum-assignment-card-img">
          <ImageWithFallback
            src={
              image.length > 0
                ? image
                : "https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/assessment-assets%2Fshutterstock_1501576922.jpg?alt=media&token=85415939-70ed-4213-bdbc-ba3a4d616797"
            }
            alt=""
          />
        </div>

        <div className="curriculum-assignment-card-header">
          <div>
            {localizedCategory && <i>{localizedCategory}</i>}
            <h6>
              <Link to={`/modules/${id}`}>{localizedTitle}</Link>
            </h6>
          </div>
        </div>
        {
          // check if html is not empty
          renderWhatLearn() &&
          <label>
            What you will learn:
          </label>}
        <div className="curriculum-assignment-card-content" dangerouslySetInnerHTML={{ __html: renderWhatLearn() }}>
          {/* {renderDescription()} */}

        </div>
      </div>

      <div className="curriculum-assignment-card-footer">
        {renderCompletionStatus()}
        {localizedTime && (
          <div className="curriculum-assignment-card-footer-time">
            <FontAwesomeIcon icon={faClock} />
            <span>{localizedTime}</span>
          </div>
        )}
      </div>
    </div>
  )
}

const CurriculumPrograms = () => {
  const { formatMessage, locale } = useIntl()
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("ALL")
  const [completionStatusSort, setCompletionStatusSort] = useState("ALL")
  const scrollRef = useRef(null) // Create a ref for scrolling
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 12

  const { firebase, subdomainConfig } = useContext(FirebaseContext)

  const { programListWithUserCompletion, areProgramsLoading } = useSchoolFilteredProgramsDB(firebase, subdomainConfig.subdomainSchoolId)

  const searcher = new FuzzySearch(programListWithUserCompletion, ["title"], {
    caseSensitive: false,
  })

  let filteredPrograms = search ? searcher.search(search) : programListWithUserCompletion

  if (category !== "ALL") {
    filteredPrograms = filteredPrograms.filter((program) => program.category === category)
  }

  if (completionStatusSort !== "ALL") {
    filteredPrograms = filteredPrograms.filter((program) => program.status === completionStatusSort)
  }

  const uniqueCategories = ["ALL", ...new Set(programListWithUserCompletion.map((program) => program.category).filter(category => category))].sort((a, b) => {
    if (a === "ALL") return -1
    if (b === "ALL") return 1
    return a.localeCompare(b)
  })

  const categoryToText = { "ALL": formatMessage({ id: "curriculum-ALL", defaultMessage: "ALL" }) }
  programListWithUserCompletion.forEach(program => {
    if (locale === "es-ES" && program.category && program.category_es) {
      categoryToText[program.category] = program.category_es
    } else if (locale === "en-US" && program.category) {
      categoryToText[program.category] = program.category
    }
  })

  const renderCurriculum = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentPrograms = filteredPrograms.slice(startIndex, endIndex)

    return currentPrograms.map((program, index) => <CurriculumProgramCard key={index} program={program} />)
  }

  const handleCurriculumTypeChange = (type) => {
    setSearch("")
    setCategory("ALL")
    setCompletionStatusSort("ALL")
    // setCurriculumType(type)
    setCurrentPage(1)
  }

  const handleCategoryChange = (event) => {
    setCategory(event.target.value)
  }

  const handleCompletionStatusSortChange = (status) => {
    setSearch("") // Clear the search input field
    setCategory("ALL") // Reset the category to "ALL"
    setCompletionStatusSort(status) // Update the completion status sort
    setCurrentPage(1) // Reset to the first page
  }

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1)
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }, 100)
  }

  const totalPages = Math.ceil(filteredPrograms.length / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, filteredPrograms.length)

  return (
    <>
      <div className="curriculum-filters" ref={scrollRef}>
        <div className="search">
          <input
            type="text"
            placeholder={formatMessage({ id: "curriculum-program-name", defaultMessage: "Program Name" })}
            value={search}
            onChange={(e) => setSearch(e.target.value)} />
          <div className="search-button">
            <FontAwesomeIcon icon={faSearch} color="#363636" size="lg" />
          </div>
        </div>
        <Dropdown
          placeholder="Select Category"
          selection
          options={uniqueCategories.map((cat) => ({
            key: cat,
            text: categoryToText[cat] || cat,
            value: cat,
          }))}
          icon="chevron down"
          value={category}
          onChange={(e, { value }) => handleCategoryChange({ target: { value } })}
        />
        <div className="radio-group">
          <FormattedMessage id="curriculum-sort-by" defaultMessage="Sort by:" />{" "}
          <div>
            <label>
              <RadioButton
                inputId="all"
                name="choice"
                value="ALL"
                checked={completionStatusSort === "ALL"}
                onChange={() => handleCompletionStatusSortChange("ALL")}
              />
              <span>
                <FormattedMessage id="curriculum-all" defaultMessage="All" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="in_progress"
                name="choice"
                value="in_progress"
                checked={completionStatusSort === "in_progress"}
                onChange={() => handleCompletionStatusSortChange("in_progress")}
              />
              <span>
                <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="completed"
                name="choice"
                value="completed"
                checked={completionStatusSort === "completed"}
                onChange={() => handleCompletionStatusSortChange("completed")}
              />
              <span>
                <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
              </span>
            </label>
          </div>
          <div>
            <label>
              <RadioButton
                inputId="not_started"
                name="choice"
                value="not_started"
                checked={completionStatusSort === "not_started"}
                onChange={() => handleCompletionStatusSortChange("not_started")}
              />
              <span>
                <FormattedMessage id="curriculum-not-started" defaultMessage="Not Started" />
              </span>
            </label>
          </div>
        </div>
      </div>
      {areProgramsLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "20vh" }}>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#00b8ef" />
        </div>
      ) : (
        <div className="curriculum-content-grid">{renderCurriculum()}</div>
      )}
      <div className="pagination">
        <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>
          <FontAwesomeIcon icon={faChevronLeft} color="#406C70" />
        </button>
        <span>
          {startItem} - {endItem} <FormattedMessage id="curriculum-of" defaultMessage="of" /> {filteredPrograms.length}
        </span>
        <button onClick={() => handlePageChange("next")} disabled={currentPage === totalPages}>
          <FontAwesomeIcon icon={faChevronRight} color="#406C70" />
        </button>
      </div>
    </>
  )
}

const CurriculumProgramCard = ({ program }) => {
  const { id, title, title_es, description, description_es, category, category_es, time, time_es, image, status } = program
  const { locale } = useIntl()

  // Localize all displayed text
  let localizedTitle, localizedDescription, localizedCategory, localizedTime
  if (locale === "en-US" || !locale) {
    localizedTitle = title
    localizedDescription = description
    localizedCategory = category
    localizedTime = time
  } else if (locale === "es-ES") {
    localizedTitle = title_es || title
    localizedDescription = description_es || description
    localizedCategory = category_es || category
    localizedTime = time_es || time
  }

  const renderDescription = () => {
    const descriptionMaxLength = 75
    const localizedTruncatedText = truncateText(localizedDescription, descriptionMaxLength)
    return DOMPurify.sanitize(localizedTruncatedText)
  }

  const renderCompletionStatus = () => {
    switch (status) {
      case "completed":
        return (
          <span className="curriculum-program-card-footer-status" style={{ color: "#2DB76D" }}>
            <FontAwesomeIcon icon={faCheckCircle} />
            <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
          </span>
        )
      case "in_progress":
        return (
          <span className="curriculum-program-card-footer-status" style={{ color: "#E7812E" }}>
            <FontAwesomeIcon icon={faSpinnerThird} />
            <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
          </span>
        )
      // case "not_started":
      //   return <span className="curriculum-program-card-footer-status">Not Started</span>
      default:
        return <span className="curriculum-program-card-footer-status"></span>
    }
  }

  const handleCardClick = () => {
    navigate(`${locale === "es-ES" ? "/es-ES" : ""}/programs/${id}`)
  }

  return (
    <div className="curriculum-program-card" onClick={handleCardClick}>
      <div>
        <div className="curriculum-program-card-img">
          <img src={image ?? "https://imgix.cosmicjs.com/73afba70-598a-11ef-b1ea-f56c65dfade9-unnamed-12.jpg"} alt="" />
        </div>

        <div className="curriculum-program-card-header">
          <div>
            {localizedCategory && <i>{localizedCategory}</i>}
            <h6>
              <Link to={`/programs/${id}`}>{localizedTitle}</Link>
            </h6>
          </div>
        </div>

        <div className="curriculum-program-card-content">
          {renderDescription()}
        </div>
      </div>
      <div className="curriculum-program-card-footer">
        {renderCompletionStatus()}
        {localizedTime && (
          <div className="curriculum-program-card-footer-time">
            <FontAwesomeIcon icon={faClock} />
            <span>{localizedTime}</span>
          </div>
        )}
      </div>
    </div>
  )
}

const CurriculumAssignmentCard = ({ assignment }) => {
  const {
    short_description,
    short_description_es,
    module_id,
    title,
    title_es,
    category,
    category_es,
    time,
    time_es,
    status,
    image,
    due_at,
    creator_first_name,
    creator_last_name,
  } = assignment
  const { locale } = useIntl()
  const formattedDueDate = new Date(due_at).toLocaleDateString(locale)

  // Localize all displayed text
  let localizedTitle, localizedDescription, localizedCategory, localizedTime
  if (locale === "en-US" || !locale) {
    localizedTitle = title
    localizedDescription = short_description
    localizedCategory = category
    localizedTime = time
  } else if (locale === "es-ES") {
    localizedTitle = title_es || title
    localizedDescription = short_description_es || short_description
    localizedCategory = category_es || category
    localizedTime = time_es || time
  }

  const renderDescription = () => {
    const descriptionMaxLength = 75
    const localizedTruncatedText = truncateText(localizedDescription, descriptionMaxLength)
    return DOMPurify.sanitize(localizedTruncatedText)
  }

  const calculateDaysRemaining = () => {
    if (!due_at) return null
    const today = new Date()
    const dueDate = new Date(due_at)
    const timeDiff = dueDate - today
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
    return daysRemaining
  }

  const daysRemaining = calculateDaysRemaining()

  const getDaysRemainingLabelStyle = () => {
    if (daysRemaining === null) return {}
    if (daysRemaining < 0) {
      return { backgroundColor: "#FFC2C2", color: "#363636" }
    } else if (daysRemaining <= 3) {
      return { backgroundColor: "#fff3c2", color: "#363636" }
    } else {
      return { backgroundColor: "#E5E5E5", color: "#363636" }
    }
  }

  const renderDaysRemainingLabel = () => {
    if (daysRemaining === null) return null
    if (daysRemaining < 0) {
      return (
        <span style={getDaysRemainingLabelStyle()}>
          <FormattedMessage id="curriculum-past-due" defaultMessage="Past Due" />
        </span>
      )
    } else if (daysRemaining === 0) {
      return (
        <span style={getDaysRemainingLabelStyle()}>
          <FormattedMessage id="curriculum-due-today" defaultMessage="Due Today" />
        </span>
      )
    } else if (daysRemaining === 1) {
      return (
        <span style={getDaysRemainingLabelStyle()}>
          <FormattedMessage id="curriculum-1-day-remaining" defaultMessage="1 Day Remaining" />
        </span>
      )
    } else {
      return (
        <span style={getDaysRemainingLabelStyle()}>
          {`${daysRemaining} `}<FormattedMessage id="curriculum-days-remaining" defaultMessage="Days Remaining" />
        </span>
      )
    }
  }

  const renderCompletionStatus = () => {
    switch (status) {
      case "completed":
        return (
          <span className="curriculum-assignment-card-footer-status" style={{ color: "#2DB76D" }}>
            <FontAwesomeIcon icon={faCheckCircle} />
            <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
          </span>
        )
      case "in_progress":
        return (
          <span className="curriculum-assignment-card-footer-status" style={{ color: "#E7812E" }}>
            <FontAwesomeIcon icon={faSpinnerThird} />
            <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
          </span>
        )
      default:
        return <span className="curriculum-assignment-card-footer-status"></span>
    }
  }

  const handleCardClick = () => {
    navigate(`${locale === "es-ES" ? "/es-ES" : ""}/modules/${module_id}`)
  }

  return (
    <div className="curriculum-assignment-card" onClick={handleCardClick}>
      <div>
        <div className="curriculum-assignment-card-img">
          <ImageWithFallback src={image ?? "https://imgix.cosmicjs.com/73afba70-598a-11ef-b1ea-f56c65dfade9-unnamed-12.jpg"} alt="" />
        </div>

        <div className="curriculum-assignment-card-header">
          <div>
            {localizedCategory && <i>{localizedCategory}</i>}
            <h6>
              <Link to={`/modules/${module_id}`}>{localizedTitle}</Link>
            </h6>
          </div>
        </div>
        <div className="curriculum-assignment-card-content">
          {renderDescription()}
        </div>
        <div className="curriculum-assignment-card-assignment-details">
          <span>
            <FormattedMessage id="curriculum-assigned-by" defaultMessage="Assigned by:" />{" "}
            <b>
              {creator_first_name ?? ""} {creator_last_name ?? ""}
            </b>
          </span>
          {due_at && (
            <>
              <span className="curriculum-assignment-card-due-date">
                <FormattedMessage id="curriculum-due-date" defaultMessage="Due Date:" /> <b>{formattedDueDate}</b>
              </span>
              {status !== "completed" && daysRemaining !== null && <div className="days-remaining-label">{renderDaysRemainingLabel()}</div>}
            </>
          )}
        </div>
      </div>

      <div className="curriculum-assignment-card-footer">
        {renderCompletionStatus()}
        {localizedTime && (
          <div className="curriculum-assignment-card-footer-time">
            <FontAwesomeIcon icon={faClock} />
            <span>{localizedTime}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkReadinessTab
